import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const getToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('adminSession'))?.token || null;
  } catch (e) {
    return null;
  }
};

function AdminDocumentViews() {
  const [tab, setTab] = useState('documents'); // documents | videos
  const [views, setViews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const path = tab === 'videos' ? 'lesson-views' : 'pdf-views';
      const res = await axios.get(`${apiUrl}/api/admin/${path}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { search, page },
      });
      setViews(res.data.views || []);
      setTotal(res.data.total || 0);
      setLimit(res.data.limit || 50);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the views.');
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [tab, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setPage(1); }, [search, tab]);

  const pages = Math.max(1, Math.ceil(total / limit));
  const tabBtn = (id, label) => (
    <button type="button" className={`btn rounded-pill py-9 px-20 ${tab === id ? 'btn-main' : 'btn-outline-main'}`} onClick={() => setTab(id)}>
      {label}
    </button>
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb mb-24">
            <ul className="flex-align gap-4">
              <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
              <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 fw-normal text-15">Document Views</span></li>
            </ul>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          <div className="card mb-24">
            <div className="card-body d-flex flex-wrap gap-12 flex-between">
              <div className="d-flex gap-8">
                {tabBtn('documents', 'Documents')}
                {tabBtn('videos', 'Videos')}
              </div>
              <input
                type="text"
                className="form-control py-9"
                style={{ maxWidth: 420 }}
                placeholder={tab === 'videos' ? 'Search student, lesson, course or IP address…' : 'Search student, username, document or IP address…'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">{tab === 'videos' ? 'Who watched lesson videos' : 'Who opened course documents'} ({total})</h5>
              <div className="text-13 text-gray-500">
                {tab === 'videos'
                  ? 'Every time a student opens a lesson video. Opening the same lesson again within 5 minutes counts once. Newest first.'
                  : 'Every time a student opens a resource or case study in the protected viewer. Newest first.'}
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : views.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No views recorded yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 align-middle">
                    {tab === 'videos' ? (
                      <>
                        <thead>
                          <tr><th>When</th><th>Student</th><th>Lesson</th><th>Course / batch</th><th>IP address</th></tr>
                        </thead>
                        <tbody>
                          {views.map((v) => (
                            <tr key={v.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{new Date(v.viewed_at).toLocaleString()}</td>
                              <td>{v.person || '—'} <span className="text-13 text-gray-400">({v.username || v.student_id})</span></td>
                              <td style={{ maxWidth: 280, whiteSpace: 'normal' }}>{v.lesson || '—'}</td>
                              <td style={{ maxWidth: 260, whiteSpace: 'normal' }}>{v.course || '—'}{v.batch ? <span className="text-13 text-gray-400"> · {v.batch}</span> : null}</td>
                              <td>{v.ip || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    ) : (
                      <>
                        <thead>
                          <tr><th>When</th><th>Student</th><th>Document</th><th>Type</th><th>IP address</th></tr>
                        </thead>
                        <tbody>
                          {views.map((v) => (
                            <tr key={v.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{new Date(v.viewed_at).toLocaleString()}</td>
                              <td>{v.person || '—'} <span className="text-13 text-gray-400">({v.username || v.user_id})</span></td>
                              <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{v.title || v.file}</td>
                              <td>{v.kind === 'case_study' ? 'Case study' : 'Resource'}</td>
                              <td>{v.ip || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    )}
                  </table>
                </div>
              )}
            </div>
            {pages > 1 && (
              <div className="card-footer d-flex flex-between align-items-center">
                <span className="text-13 text-gray-500">Page {page} of {pages}</span>
                <div className="d-flex gap-8">
                  <button type="button" className="btn btn-sm btn-outline-main" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                  <button type="button" className="btn btn-sm btn-outline-main" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminDocumentViews;
